import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Loader from 'rsuite/Loader';
import Message from 'rsuite/Message';
import { useToaster } from 'rsuite/esm/toaster';
import { getHeaders } from '../../helpers/helper';
import axiosInstance from '../../shared/config/axiosInstance';
import SearchBox from './SearchBox';
import Subheading from './Subheading';
import DocumentLink from './DocumentLink';
import FloatingButton from './FloatingButton';
import './MakeGuidePage.css';

const MakeGuidePage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const [blobs, setBlobs] = useState<{ [key: string]: { name: string; url: string }[] }>({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const parseBlobs = (blobs: string[]) => {
    const baseDocumentUrl = process.env.REACT_APP_BASE_DOCUMENT_URL || "";
    const groupedBlobs: { [key: string]: { name: string; url: string }[] } = {};
    blobs.forEach(blob => {
      const relativePath = blob.replace(baseDocumentUrl, "");
      const [folder, ...fileParts] = relativePath.split("/");
      const fileName = fileParts.join("/");
      if (!groupedBlobs[folder]) {
        groupedBlobs[folder] = [];
      }
      groupedBlobs[folder].push({ name: fileName, url: blob });
    });
    return groupedBlobs;
  };

  useEffect(() => {
    const fetchBlobs = async () => {
      setLoading(true);
      try {
        const headers = await getHeaders(getAccessTokenSilently);
        const response = await axiosInstance.get('/blob', { headers });
        const groupedBlobs = parseBlobs(response.data);
        setBlobs(groupedBlobs);
      } catch (error) {
        console.error('Error fetching blobs:', error);
        toaster.push(
          <Message showIcon type='error' closable>
            Error fetching blobs.
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );
      } finally {
        setLoading(false);
      }
    };
    fetchBlobs();
  }, [getAccessTokenSilently, toaster]);

  const handleSearch = (query: string) => {
    setSearchQuery(query.toLowerCase());
  };

  const filteredBlobs = Object.keys(blobs).reduce((acc, folder) => {
    const filteredDocs = blobs[folder].filter(doc =>
      doc.name.toLowerCase().includes(searchQuery)
    );
    if (filteredDocs.length > 0) {
      acc[folder] = filteredDocs;
    }
    return acc;
  }, {} as { [key: string]: { name: string; url: string }[] });

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="ml-lg-5 mt-md-3 text-left">
      <h1>Make Guides</h1>
      <SearchBox className="search-box mt-md-1 mb-md-3" onSearch={handleSearch} />
      {Object.keys(filteredBlobs).map(folder => (
        <Subheading key={folder} title={folder}>
          <div className="document-grid">
            {filteredBlobs[folder].map((doc, index) => (
              <DocumentLink key={index} name={doc.name} url={doc.url} />
            ))}
          </div>
        </Subheading>
      ))}
      <FloatingButton />
    </div>
  );
};

export default MakeGuidePage;